// Coalesce bursts of real foreground input. No recurring idle/polling timer:
// one pending activity renewal runs after 1s quiet, or after 30s of continuous use.
export function watchNativeActivity(target, extend, { now = Date.now, schedule = setTimeout, cancel = clearTimeout } = {}) {
    let timer, firstInput, stopped = false, pending = false, inFlight = false;
    const clear = () => { cancel(timer); timer = undefined; firstInput = undefined; pending = false; };
    const flush = async () => {
        timer = undefined;
        if (stopped || target.visibilityState !== 'visible' || !pending) { clear(); return; }
        if (inFlight) return; // The current request's completion will schedule newer input.
        pending = false; firstInput = undefined; inFlight = true;
        try { await extend(); } catch { /* No automatic retry. The API handles authentication failures. */ }
        finally {
            inFlight = false;
            if (!stopped && pending && target.visibilityState === 'visible') timer = schedule(flush, 1000);
        }
    };
    const activity = event => {
        if (event && (!event.isTrusted || event.repeat)) return;
        if (stopped || target.visibilityState !== 'visible') return;
        pending = true;
        firstInput ??= now();
        cancel(timer);
        timer = schedule(flush, Math.max(0, Math.min(1000, firstInput + 30000 - now())));
    };
    const visibility = () => {
        if (target.visibilityState === 'visible') activity();
        else clear();
    };
    const events = ['pointerdown', 'keydown', 'wheel'];
    for (const name of events) target.addEventListener(name, activity, { capture: true, passive: true });
    target.addEventListener('visibilitychange', visibility);
    activity(); // Opening/resuming an authenticated app counts as active use.
    return () => {
        stopped = true; clear();
        for (const name of events) target.removeEventListener(name, activity, true);
        target.removeEventListener('visibilitychange', visibility);
    };
}
